import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DatasetFormStatus } from 'src/app/@shared/form/base-form.component';
import { DynamicFormFieldModel } from 'src/app/@shared/form/dynamic-form-field/dynamic-form-field.model';
import { DynamicFormValues, FormFieldValue } from 'src/app/@shared/form/form.types';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { FormFieldModel } from 'src/app/@shared/form/form-field/form-field.model';
import { createDynamicForm, createFormControl } from 'src/app/@shared/form/form-helper';

@Component({
  selector: 'proto-dynamic-form-field',
  standalone: false,
  styleUrls: ['./dynamic-form-field.component.scss'],
  templateUrl: './dynamic-form-field.component.html',
})
export class DynamicFormFieldComponent {
  @Input() public formArray: FormArray;
  @Input() public field: DynamicFormFieldModel;
  @Input() public status?: DatasetFormStatus;
  @Input() public isInSplitView = false;
  @Input() public changedSubFormFields: Record<string, boolean>[] = [];

  @Output() public valueUpdated: EventEmitter<FormFieldValue | FormFieldValue[]> = new EventEmitter<FormFieldValue | FormFieldValue[]>();
  @Output() public openModal: EventEmitter<{ field: FormFieldModel; formGroupIndex?: number }>
    = new EventEmitter<{ field: FormFieldModel; formGroupIndex?: number }>();

  public subFields: FormFieldModel[] = [];
  public hasMultipleFields: boolean; // (contactPerson, phone, ...)
  public groupHasChanged: boolean;

  private isInitialized = false;

  // index based for simple form arrays (email, website, ...)
  // key based for extended form arrays (contactPerson, phone, ...)
  // eslint-disable-next-line accessor-pairs
  @Input() set values(fieldValues: DynamicFormValues[]) {
    if (!this.isInitialized) {
      this.isInitialized = true;
      this.init();
    }

    this.setDynamicFields(fieldValues);
  }

  // eslint-disable-next-line accessor-pairs
  @Input() set changedSubForms(updated: boolean[]) {
    this.groupHasChanged = updated?.some((changed) => { return changed; });
  }

  public init(): void {
    if (this.field?.dynamicFieldConfig?.fields?.length) {
      this.subFields = this.field.dynamicFieldConfig.fields;
      this.hasMultipleFields = this.subFields.length > 1;
    } else {
      console.error('field config(s) missing');
    }
  }

  // array of values inside form group (e.g. phone field)
  public asFormGroupControls(formGroup: FormGroup): FormControl[] {
    return Object.keys(formGroup.controls).map((key) => {
      return formGroup.controls[key] as FormControl;
    });
  }

  // array of values inside form array (e.g. multiple email fields)
  public asFormArrayControls(formArray: FormArray): FormControl[] {
    return (formArray?.controls || []) as FormControl[];
  }

  // array of form groups inside form array (e.g. multiple phone fields)
  public asFormGroups(formArray: FormArray): FormGroup[] {
    return (formArray?.controls || []) as FormGroup[];
  }

  public asFormField(item: FormFieldModel): FormFieldModel {
    return item;
  }

  public isSubFieldChanged(groupIndex: number, subFieldId: string): boolean {
    return !!this.changedSubFormFields?.[groupIndex]?.[subFieldId];
  }

  public isSubFieldBlocked(subFieldId: string): boolean {
    if (this.field?.id !== 'contactPersons') {
      return false;
    }

    return this.status === 'to-be-reviewed' && (subFieldId === 'firstName' || subFieldId === 'lastName');
  }

  public addDynamicField(values: DynamicFormValues = {}): void {
    let control: FormControl | FormGroup;

    if (this.hasMultipleFields) {
      control = createDynamicForm(null, this.subFields, values, this.field.dynamicFieldConfig.groupValidators);
    } else {
      const [subField] = this.subFields;
      const controlValue = values[subField.id];

      control = createFormControl(null, subField, controlValue);
    }

    if (control) {
      this.formArray.push(control);
    }
  }

  public removeDynamicField(index: number): void {
    this.formArray.removeAt(index);

    // mark as dirty to trigger correct form validation
    this.formArray.markAsDirty();
    this.valueUpdated.emit(null);
  }

  // skip when values is not set/empty (new/create form)
  private setDynamicFields(values: DynamicFormValues[]): void {
    if (!values?.length) {
      return;
    }

    this.formArray.clear();

    for (const formValues of values) {
      this.addDynamicField(formValues);
    }
  }
}
